import { ChatAttachments, type ChatAttachment } from "./ChatAttachments";
import { ArrowUp, LoaderCircle, Square } from "lucide-react";
import { memo, useLayoutEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/shadcn/Button/Button";
import { ChatSkillSuggestions } from "./ChatSkillSuggestions";
import { useChatSkillMentions } from "@/hooks/ai-chat/useChatSkillMentions";
import type { SkillSummary } from "@/lib/data/schemas/skills";

import type { QueuedChatMessage } from "@/hooks/ai-chat/useAiChatMessageQueue";

interface ChatComposerProps {
  onSend: (
    text: string,
    fileContentIds?: string[],
    fileReferences?: QueuedChatMessage["fileReferences"],
  ) => Promise<unknown>;
  allowAttachments?: boolean;
  onStop?: () => Promise<unknown>;
  isRunning?: boolean;
  isSending?: boolean;
  canSteer?: boolean;
  disabled?: boolean;
  initialDraft?: string;
  onDraftUsed?: () => void;
  previewSkills?: SkillSummary[];
}

export const ChatComposer = memo(function ChatComposer({
  onSend,
  allowAttachments = true,
  onStop,
  isRunning,
  isSending,
  disabled,
  initialDraft,
  onDraftUsed,
  previewSkills,
}: ChatComposerProps) {
  const [attachments, setAttachments] = useState<ChatAttachment[]>([]);
  const [attachmentBusy, setAttachmentBusy] = useState(false);
  const [draft, setDraft] = useState(initialDraft ?? "");
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const mentions = useChatSkillMentions({
    draft,
    disabled,
    previewSkills,
    onInsert: (value, caret) => {
      setDraft(value);
      requestAnimationFrame(() => {
        textarea.current?.focus();
        textarea.current?.setSelectionRange(caret, caret);
      });
    },
  });
  const busy = submitting || isSending;
  const submissionInFlight = useRef(false);
  const canSend = !!draft.trim() && !busy && !attachmentBusy && !disabled;

  useLayoutEffect(() => {
    if (!initialDraft) return;
    setDraft(initialDraft);
    textarea.current?.focus();
    onDraftUsed?.();
  }, [initialDraft, onDraftUsed]);

  useLayoutEffect(() => {
    const input = textarea.current;
    if (!input) return;
    const resize = () => {
      input.style.height = "0px";
      const contentHeight = draft ? input.scrollHeight : 32;
      input.style.height = `${Math.min(200, Math.max(32, contentHeight))}px`;
      input.style.overflowY = contentHeight > 200 ? "auto" : "hidden";
    };
    resize();
    let width = input.clientWidth;
    const observer = new ResizeObserver(() => {
      if (input.clientWidth === width) return;
      width = input.clientWidth;
      resize();
    });
    observer.observe(input);
    return () => {
      observer.disconnect();
    };
  }, [draft]);

  const send = async () => {
    if (!canSend || submissionInFlight.current) return;
    submissionInFlight.current = true;
    setSubmitting(true);
    setError(undefined);
    const submittedDraft = draft.trim();
    const text = mentions.serialize(submittedDraft);
    mentions.close();
    const submittedAttachments = attachments;
    setDraft("");
    setAttachments([]);
    try {
      await onSend(
        text,
        submittedAttachments.length
          ? submittedAttachments.map((file) => file.id)
          : undefined,
        ...(submittedAttachments.length
          ? [
              submittedAttachments.map((file) => ({
                fileContentId: file.id,
                fileName: file.name,
                processingId: file.processingId,
              })),
            ]
          : []),
      );
    } catch (cause) {
      setDraft((current) =>
        current ? `${submittedDraft}\n\n${current}` : submittedDraft,
      );
      setAttachments((current) => [
        ...submittedAttachments,
        ...current.filter(
          (file) => !submittedAttachments.some((sent) => sent.id === file.id),
        ),
      ]);
      setError(
        cause instanceof Error
          ? cause.message
          : "Your message could not be sent. Please try again.",
      );
    } finally {
      submissionInFlight.current = false;
      setSubmitting(false);
      textarea.current?.focus();
    }
  };

  return (
    <div className="mx-auto w-full max-w-[60rem] shrink-0 px-5 pb-4 md:px-8 md:pb-5">
      {error && (
        <p role="alert" className="text-destructive mb-2 text-sm">
          {error}
        </p>
      )}
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void send();
        }}
        className="bg-background border-border focus-within:border-foreground/30 relative rounded-2xl border p-3 shadow-[0_2px_12px_-5px_rgba(0,0,0,0.12)] transition-colors"
      >
        <ChatSkillSuggestions mentions={mentions} />
        {allowAttachments && (
          <ChatAttachments
            attachments={attachments}
            onChange={setAttachments}
            onBusyChange={setAttachmentBusy}
            disabled={disabled}
            onSelectSkill={(nativeName) => {
              setDraft(
                (current) =>
                  `${current}${current && !/\s$/.test(current) ? " " : ""}$${nativeName} `,
              );
              requestAnimationFrame(() => textarea.current?.focus());
            }}
          />
        )}
        <textarea
          ref={textarea}
          rows={1}
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            mentions.update(
              event.target.value,
              event.target.selectionStart,
              event.target.selectionEnd,
            );
          }}
          onClick={(event) => {
            mentions.update(
              event.currentTarget.value,
              event.currentTarget.selectionStart,
              event.currentTarget.selectionEnd,
            );
          }}
          onKeyUp={(event) => {
            if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key))
              mentions.update(
                event.currentTarget.value,
                event.currentTarget.selectionStart,
                event.currentTarget.selectionEnd,
              );
          }}
          onBlur={mentions.close}
          aria-autocomplete="list"
          aria-controls={mentions.open ? mentions.id : undefined}
          aria-activedescendant={
            mentions.open && mentions.items.length
              ? `${mentions.id}-${mentions.selectedIndex}`
              : undefined
          }
          disabled={disabled}
          aria-label={isRunning ? "Add instructions" : "Message LawWorks AI"}
          placeholder={
            isRunning
              ? "Add instructions while LawWorks works…"
              : "Ask LawWorks to find, review, or take action…"
          }
          className="placeholder:text-muted-foreground/80 block max-h-50 min-h-8 w-full min-w-0 resize-none bg-transparent py-1 pr-24 pl-1 text-sm leading-6 outline-none disabled:opacity-60"
          onKeyDown={(event) => {
            if (mentions.onKeyDown(event)) return;
            if (
              event.key === "Enter" &&
              !event.shiftKey &&
              !event.nativeEvent.isComposing
            ) {
              event.preventDefault();
              void send();
            }
          }}
        />
        <div className="absolute right-3 bottom-3 flex shrink-0 items-center">
          {isRunning && (
            <span className="sr-only">Working on your request</span>
          )}
          <div className="ml-auto flex items-center gap-2">
            {isRunning && onStop ? (
              <Button
                type="button"
                size="icon"
                aria-label="Stop response"
                className="size-8 rounded-full"
                onClick={() => {
                  void onStop().catch((cause: unknown) => {
                    setError(
                      cause instanceof Error
                        ? cause.message
                        : "Could not stop the response.",
                    );
                  });
                }}
              >
                <Square className="size-3 fill-current" />
              </Button>
            ) : (
              <Button
                type="submit"
                size="icon"
                disabled={!canSend}
                className="size-8 rounded-full"
                aria-label="Send message"
              >
                {busy ? <LoaderCircle className="animate-spin" /> : <ArrowUp />}
              </Button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
});
